import { useCallback, useEffect, useState } from 'react';

export interface LoadGate {
  /** True until markLoaded() is called (or after retry() restarts the gate). */
  loading: boolean;
  /** True when loading has exceeded timeoutMs without markLoaded(). */
  timedOut: boolean;
  /** Bump this into the data-fetch effect deps so retry() resubscribes. */
  attempt: number;
  markLoaded: () => void;
  retry: () => void;
}

/**
 * Loading gate with a hang timeout. Firestore listeners never report
 * connectivity errors - they silently wait - so any UI gated on a snapshot
 * callback needs a timeout path to avoid an infinite spinner.
 *
 * Usage:
 *   const gate = useLoadGate();
 *   useEffect(() => {
 *       const unsub = subscribeThings((data) => { setThings(data); gate.markLoaded(); });
 *       return unsub;
 *   }, [gate.attempt]);
 *   if (gate.timedOut) return <LoadTimeout onRetry={gate.retry} />;
 *   if (gate.loading) return <div>Loading...</div>;
 */
export function useLoadGate(timeoutMs = 8000): LoadGate {
  const [loading, setLoading] = useState(true);
  const [timedOut, setTimedOut] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!loading) return;
    const t = window.setTimeout(() => setTimedOut(true), timeoutMs);
    return () => window.clearTimeout(t);
  }, [loading, attempt, timeoutMs]);

  const markLoaded = useCallback(() => {
    setLoading(false);
    setTimedOut(false);
  }, []);

  const retry = useCallback(() => {
    setLoading(true);
    setTimedOut(false);
    setAttempt((a) => a + 1);
  }, []);

  return { loading, timedOut, attempt, markLoaded, retry };
}

/**
 * Timeout adapter for a hook-provided `loading` flag (useRules, useSettings, ...).
 * Returns true when the flag has stayed true for longer than timeoutMs.
 * Pair with a remount-style retry (key bump) so the data hooks resubscribe.
 */
export function useGateTimeout(loading: boolean, timeoutMs = 8000): boolean {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!loading) {
      setTimedOut(false);
      return;
    }
    const t = window.setTimeout(() => setTimedOut(true), timeoutMs);
    return () => window.clearTimeout(t);
  }, [loading, timeoutMs]);

  return timedOut;
}
